import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ORG_LOGO_BUCKET } from "@/components/ui/org-logo";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { toast } from "sonner";

export const LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const LOGO_ACCEPT = "image/png,image/jpeg,image/webp";
const LOGO_ALLOWED_MIME = new Set(["image/png", "image/jpeg", "image/webp"]);

function extensionFor(mime: string): string {
  if (mime === "image/png") return "png";
  if (mime === "image/webp") return "webp";
  return "jpg";
}

export type LogoRejection = "type" | "size";

/** Why a picked file is not an image we accept, or `null` when it is fine. */
export function validateLogoFile(file: File): LogoRejection | null {
  if (!LOGO_ALLOWED_MIME.has(file.type)) return "type";
  if (file.size > LOGO_MAX_BYTES) return "size";
  return null;
}

/**
 * Upload / remove the current organization's logo.
 *
 * The image lands in the public `org-logos` bucket at `<org_id>/logo-<stamp>.<ext>`
 * and the path is persisted on `organisations.logo_url`. The bucket's RLS is scoped
 * to the `<org_id>` folder and only lets an org owner write there, so the caller
 * gates the UI on the same tier.
 *
 * Every upload takes a fresh path: the objects are served with a long
 * `Cache-Control`, so a stable path would leave every logo already in a browser's
 * cache (the switcher's, an email client's) stale for an hour after a replacement.
 * A new path is a new URL, which every consumer of `logo_url` refetches.
 */
export function useOrgLogo() {
  const { t } = useLanguage();
  const { currentOrganization, refetch } = useOrganizationContext();
  const organizationStore = useOrganizationStore();

  const [uploading, setUploading] = useState(false);

  const persist = async (logoPath: string | null) => {
    if (!currentOrganization) return;
    const success = await organizationStore.updateOrganization(currentOrganization.id, {
      logo_url: logoPath,
    });
    if (!success) {
      throw new Error(organizationStore.error || "Failed to update organization");
    }
    // Refresh the org context so the switcher picks the change up.
    await refetch();
  };

  const reportFailure = (err: unknown, action: "upload" | "remove") => {
    if (process.env.NODE_ENV === "development") {
      // The action is passed as its own argument rather than interpolated: a value
      // in a log format string is a CRLF-injection vector, so we never build one.
      console.error("org logo action failed", action, err);
    }
    toast.error(err instanceof Error ? err.message : t.settings.organization.logoUploadFailed);
  };

  /**
   * Drop an object nothing points at any more. Best-effort: the DB row is already
   * correct, so a failed cleanup leaves a stray file but never a broken logo.
   */
  const discardObject = async (path: string) => {
    const { error } = await supabase.storage.from(ORG_LOGO_BUCKET).remove([path]);
    if (error && process.env.NODE_ENV === "development") {
      console.error("org logo: storage cleanup failed", error);
    }
  };

  /** Uploads the cropped square image and persists its path. Returns true on success. */
  const upload = async (blob: Blob, mime: string): Promise<boolean> => {
    if (!currentOrganization) return false;
    setUploading(true);
    try {
      // The blob is what the crop canvas encoded, so its own type describes the bytes we
      // are actually holding. The picked file's claimed `mime` is only a fallback for
      // environments where `toBlob` leaves the type empty.
      const contentType = blob.type || mime;
      // A fresh path per upload, so the URL changes whenever the image does.
      const path = `${currentOrganization.id}/logo-${Date.now()}.${extensionFor(contentType)}`;
      // Nothing points at the previous object once the row moves to the new path.
      const replacedPath = currentOrganization.logo_url;
      const { error } = await supabase.storage
        .from(ORG_LOGO_BUCKET)
        .upload(path, blob, { contentType, cacheControl: "3600" });
      if (error) throw error;

      try {
        await persist(path);
      } catch (err) {
        // The row still points at the old object, so the one we just wrote is unreferenced.
        // Drop it, or every failed retry leaves another stray file in the bucket.
        await discardObject(path);
        throw err;
      }
      if (replacedPath && replacedPath !== path) {
        await discardObject(replacedPath);
      }
      toast.success(t.settings.organization.logoUploaded);
      return true;
    } catch (err) {
      reportFailure(err, "upload");
      return false;
    } finally {
      setUploading(false);
    }
  };

  const remove = async (): Promise<void> => {
    const path = currentOrganization?.logo_url;
    if (!path) return;
    setUploading(true);
    try {
      // Clear the row before the object: a row pointing at a deleted file renders a
      // broken logo (404) until someone re-uploads, while a leftover object is inert.
      await persist(null);
      await discardObject(path);
      toast.success(t.settings.organization.logoRemoved);
    } catch (err) {
      reportFailure(err, "remove");
    } finally {
      setUploading(false);
    }
  };

  return { organization: currentOrganization, uploading, upload, remove };
}

export interface LogoCrop {
  /** Whether the crop dialog is open. */
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Object URL of the picked image, or null when nothing is being cropped. */
  src: string | null;
  /** MIME type the picked file claimed — the fallback when the canvas omits one. */
  mime: string;
  pick: (file: File) => void;
  close: () => void;
}

/**
 * The "pick a file → crop it" half of setting a logo: validation, the object URL
 * behind the crop dialog, and its lifecycle. Kept apart from `useOrgLogo` so the
 * section component stays a thin shell around picker + dialog + buttons.
 *
 * `onReject` is called with the reason a picked file is not usable; the caller
 * turns it into a message.
 */
export function useLogoCrop(onReject: (reason: LogoRejection) => void): LogoCrop {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [mime, setMime] = useState<string>("image/jpeg");

  // Revoke any dangling preview URL on unmount.
  useEffect(() => {
    return () => {
      if (src) URL.revokeObjectURL(src);
    };
  }, [src]);

  const close = () => {
    setOpen(false);
    setSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  // The raw file is never uploaded — the cropped square Blob is.
  const pick = (file: File) => {
    const rejection = validateLogoFile(file);
    if (rejection) {
      onReject(rejection);
      return;
    }

    setSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setMime(file.type);
    setOpen(true);
  };

  return { open, setOpen, src, mime, pick, close };
}
